import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router";
import { enquiriesApi } from "@/lib/enquiries";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageContainer } from "@/app/layouts/PageContainer";
import { DetailPage } from "@/archetypes/DetailPage";
import { toast } from "@/hooks/use-toast";

import { EnquiryHeader } from "./components/EnquiryHeader";

// ---------------------------------------------------------------------------
// SkeletonDetail
// ---------------------------------------------------------------------------

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// EnquiryDetailPage
// ---------------------------------------------------------------------------

export function EnquiryDetailPage() {
  const { id = "0" } = useParams();
  const enqId = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["enquiry", enqId],
    queryFn: () => enquiriesApi.get(enqId),
    enabled: !!enqId,
  });

  const itemsQuery = useQuery({
    queryKey: ["enquiry-items", enqId],
    queryFn: () => enquiriesApi.items(enqId),
    enabled: !!enqId,
  });

  const convert = useMutation({
    mutationFn: () => enquiriesApi.convertToQuote(enqId),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["enquiry", enqId] });
      void qc.invalidateQueries({ queryKey: ["enquiries"] });
      void qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(`Quote ${res.quoteRef} created.`);
      nav(`/quotes/${res.quoteId}`);
    },
    onError: (err: Error) => toast.error(`Convert failed: ${err.message}`),
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Enquiry not found.</p>
      </PageContainer>
    );
  }

  const canConvert = data.status === "New" || data.status === "InQuote";

  const headerActions = (
    <>
      {/* TODO(plan-ii-followup): wire to enquiry send endpoint */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span role="button" aria-disabled="true" tabIndex={0}>
              <Button variant="outline" size="sm" disabled>
                Send Email
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Send via email not yet enabled for enquiries — see Plan II runbook.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button
        onClick={() => convert.mutate()}
        disabled={!canConvert || convert.isPending}
        aria-label="Convert to Quote"
      >
        {convert.isPending ? "Converting…" : "Convert to Quote"}
      </Button>
    </>
  );



  const tabs = [
    {
      id: "enquiry",
      label: "Enquiry",
      forceMount: true,
      content: (
        <div className="space-y-8 max-w-4xl">
          <div>
            <h3 className="text-sm font-semibold mb-2 text-slate-900">Description</h3>
            <div className="text-sm text-slate-800 bg-white p-4 rounded-lg border shadow-sm">
              {data.description ? (
                <p className="whitespace-pre-wrap">{data.description}</p>
              ) : (
                <p className="text-muted-foreground italic">No description provided.</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2 text-slate-900">Items</h3>
            {itemsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading items…</p>
            ) : (itemsQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No items on this enquiry.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left py-2.5 px-4 font-medium text-slate-700">Description</th>
                      <th className="text-right py-2.5 px-4 w-24 font-medium text-slate-700">Qty</th>
                      <th className="text-left py-2.5 px-4 font-medium text-slate-700">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(itemsQuery.data ?? []).map((item) => (
                      <tr key={item.enquiryItemId} className="bg-white">
                        <td className="py-2.5 px-4">{item.description}</td>
                        <td className="py-2.5 px-4 text-right">{item.quantity ?? "—"}</td>
                        <td className="py-2.5 px-4 text-muted-foreground">{item.notes ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2 text-slate-900">Linked Quotes</h3>
            <div className="text-sm bg-white p-4 rounded-lg border shadow-sm flex items-center">
              <Link
                to={`/quotes?enquiryId=${data.enquiryId}`}
                className="text-primary hover:underline font-medium"
              >
                View quotes for enquiry #{data.enquiryId} →
              </Link>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "history",
      label: "History",
      content: (
        <div>
          <p className="text-sm text-muted-foreground">
            Audit history is available in a future plan.
          </p>
        </div>
      ),
    },
  ];

  return (
    <DetailPage
      title={data.enquiryId.toString()}
      headerOverride={<EnquiryHeader enquiry={data} />}
      tabActions={headerActions}
      tabs={tabs}
      defaultTab="enquiry"
      backTo="/enquiries"
    />
  );
}
